import { create } from 'zustand';

interface AudioState {
  soundEnabled: boolean;
  musicEnabled: boolean;
  setSoundEnabled: (enabled: boolean) => void;
  setMusicEnabled: (enabled: boolean) => void;
  playMusic: () => void;
  stopMusic: () => void;
  toggleSound: () => void;
  toggleMusic: () => void;
  playSFX: (sfxName: 'click' | 'mark' | 'claim' | 'call') => void;
}

// Background music HTMLAudioElement instance (singleton)
let bgMusic: HTMLAudioElement | null = null;

// Royalty-free WAV assets from Mixkit
const SFX_URLS = {
  click: 'https://assets.mixkit.co/active_storage/sfx/2568/2568-84.wav',
  mark: 'https://assets.mixkit.co/active_storage/sfx/2019/2019-84.wav',
  claim: 'https://assets.mixkit.co/active_storage/sfx/2018/2018-84.wav',
  call: 'https://assets.mixkit.co/active_storage/sfx/2013/2013-84.wav',
};

const getBGMusic = () => {
  if (!bgMusic) {
    // Premium loopable electronic background instrumental song
    bgMusic = new Audio('https://www.soundhelix.com/examples/mp3/SoundHelix-Song-8.mp3');
    bgMusic.loop = true;
    bgMusic.volume = 0.15; // Set volume soft in background
  }
  return bgMusic;
};

export const useAudioStore = create<AudioState>((set, get) => {
  // Load preferences from localStorage (default true)
  const savedSound = localStorage.getItem('pref_sound') !== 'false';
  const savedMusic = localStorage.getItem('pref_music') !== 'false';

  return {
    soundEnabled: savedSound,
    musicEnabled: savedMusic,

    setSoundEnabled: (enabled) => {
      localStorage.setItem('pref_sound', String(enabled));
      set({ soundEnabled: enabled });
    },

    setMusicEnabled: (enabled) => {
      localStorage.setItem('pref_music', String(enabled));
      set({ musicEnabled: enabled });
      
      const music = getBGMusic();
      if (enabled) {
        music.play().catch((err) => console.warn('Autoplay prevented:', err));
      } else {
        music.pause();
      }
    },

    playMusic: () => {
      if (!get().musicEnabled) return;
      const music = getBGMusic();
      if (music.paused) {
        music.play().catch((err) => console.warn('Autoplay prevented:', err));
      }
    },

    stopMusic: () => {
      if (bgMusic) {
        bgMusic.pause();
      }
    },

    toggleSound: () => {
      const next = !get().soundEnabled;
      get().setSoundEnabled(next);
    },

    toggleMusic: () => {
      const next = !get().musicEnabled;
      get().setMusicEnabled(next);
    },

    playSFX: (sfxName) => {
      if (!get().soundEnabled) return;
      try {
        const audio = new Audio(SFX_URLS[sfxName]);
        audio.volume = 0.3;
        audio.play().catch((err) => console.warn('SFX play blocked:', err));
      } catch (err) {
        console.warn('SFX failed to play:', err);
      }
    },
  };
});
